import cronstrue from 'cronstrue'

export type IntervalUnit = 'minutes' | 'hours' | 'days'

export const intervalUnits: IntervalUnit[] = ['minutes', 'hours', 'days']

const UNIT_TO_MINUTES: Record<IntervalUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
}

const FALLBACK_TIMEZONES = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Australia/Sydney',
]

export function describeCron(expr: string, timezone: string): { readable: string; error: string | null } {
  const trimmed = expr.trim()
  if (!trimmed) return { readable: '', error: null }

  try {
    const readable = cronstrue.toString(trimmed)
    return {
      readable: timezone ? `${readable} (${timezone})` : readable,
      error: null,
    }
  } catch (error) {
    return {
      readable: '',
      error: error instanceof Error ? error.message : 'Invalid cron expression',
    }
  }
}

export function intervalToMinutes({ value, unit }: { value: number; unit: IntervalUnit }): number {
  return value * UNIT_TO_MINUTES[unit]
}

export function minutesToInterval(minutes: number): { value: number; unit: IntervalUnit } {
  if (minutes > 0 && minutes % UNIT_TO_MINUTES.days === 0) {
    return { value: minutes / UNIT_TO_MINUTES.days, unit: 'days' }
  }

  if (minutes > 0 && minutes % UNIT_TO_MINUTES.hours === 0) {
    return { value: minutes / UNIT_TO_MINUTES.hours, unit: 'hours' }
  }

  return { value: minutes || 1, unit: 'minutes' }
}

export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function getTimezoneList(): string[] {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone')
    }
  } catch {
    // Fall through to the curated list.
  }

  return FALLBACK_TIMEZONES
}
